import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Card, CardActionArea, Skeleton } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../lib/axios';

interface ActivePromotion {
  id: string;
  productName: string;
  description: string;
  backgroundColor: string;
}

const NAVY = '#0D1B35';

export const ConsultantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: promotions = [], isLoading: promosLoading } = useQuery<ActivePromotion[]>({
    queryKey: ['consultant-active-promotions'],
    queryFn: async () => {
      const { data } = await apiClient.get<ActivePromotion[]>('/promotions/active');
      return data;
    },
  });

  const { data: stats } = useQuery<{ totalProducts: number; totalInStock: number; totalBranches: number }>({
    queryKey: ['consultant-catalog-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/products/catalog-stats');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  const name = user?.fullName ?? user?.email?.split('@')[0] ?? '';

  const [promoIndex, setPromoIndex] = useState(0);
  useEffect(() => {
    if (promotions.length <= 1) return;
    const timer = setInterval(() => setPromoIndex((p) => (p + 1) % promotions.length), 5000);
    return () => clearInterval(timer);
  }, [promotions.length]);
  const activePromo = promotions.length > 0 ? promotions[promoIndex % promotions.length] : null;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ color: NAVY }}>{greeting}{name ? `, ${name}` : ''}</Typography>
        <Typography sx={{ color: 'text.secondary' }}>Promociones activas de productos</Typography>
      </Box>

      {promosLoading ? (
        <Skeleton variant="rounded" sx={{ borderRadius: 3, height: 150, mb: 3 }} />
      ) : activePromo ? (
        <Card
          sx={{ borderRadius: 3, bgcolor: activePromo.backgroundColor, mb: 3, color: '#fff' }}
        >
          <CardActionArea
            onClick={() => navigate(`/consultant/medications?q=${encodeURIComponent(activePromo.productName)}`)}
            sx={{ p: 3, minHeight: 150 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1.6rem', mb: 1 }}>{activePromo.productName}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{activePromo.description}</Typography>
          </CardActionArea>
        </Card>
      ) : (
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>No hay promociones activas.</Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Card sx={{ p: 2.5, flex: '1 1 160px', minWidth: 140 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: NAVY }}>{stats?.totalProducts ?? '—'}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Productos en catálogo</Typography>
        </Card>
        <Card sx={{ p: 2.5, flex: '1 1 160px', minWidth: 140 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: NAVY }}>{stats?.totalInStock ?? '—'}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>En stock</Typography>
        </Card>
        <Card sx={{ p: 2.5, flex: '1 1 160px', minWidth: 140 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: NAVY }}>{stats?.totalBranches ?? '—'}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Sucursales</Typography>
        </Card>
      </Box>
    </Box>
  );
};
